import { Alert, Chips, Modal } from '@gupshup-frontend/gupshup-ts-components';
import { formatDate, mapTemplate } from '../../helper/utils';
import { TONE_TO_CHIP_COLOR, isEditable, statusMeta } from '../../helper/templateStatus';
import { toFormBodyNumbering } from '../../helper/templateVariables';
import TemplatePreview from '../../components/TemplatePreview';

const EM_DASH = '—';

const orDash = (value) => (value === undefined || value === null || value === '' ? EM_DASH : value);

// button_type carries the literal string "NONE" for a template with no button component (see
// List.js's own Buttons column) — treat that the same as a missing value.
const buttonsOrDash = (value) => (value && value !== 'NONE' ? value : EM_DASH);

// creation_time/updation_time arrive as epoch milliseconds. List.js's own formatDateTime does the
// same date+time composition on top of formatDate, but it is local/unexported there and List.js
// is off-limits to touch for this task — so this is a same-shaped local copy, not a shared import.
const formatEpochMs = (epochMs) => {
  if (!epochMs) return EM_DASH;
  const time = new Date(epochMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${formatDate(epochMs)} ${time}`;
};

// Read-only detail view for one template. Mounted by Templates/index.js only while
// pendingAction.intent === 'view'; onClose is how the parent unmounts it. onEdit hands the same
// template to the create/edit-form task by switching pendingAction.intent to 'edit' — that task
// owns rendering the form, this component never builds one itself.
const DetailModal = ({ template, onClose, onEdit }) => {
  const meta = statusMeta(template?.status);
  // Upstream only permits an update in these three states (see templateStatus.js EDITABLE_STATUSES)
  // — same rule, and the same tooltip wording, as List.js's own row-level Edit action.
  const editable = isEditable(template?.status);

  // Enterprise stores the body renumbered relative to the header (see
  // helper/templateVariables.js's toFormBodyNumbering) — shown here the same {{1}}-based way the
  // console and this app's own edit form show it, not Enterprise's raw numbering.
  const normalizedBody = template ? toFormBodyNumbering(template.header, template.body) : template?.body;

  // mapTemplate (helper/utils.js) already adapts an Enterprise list row into the synthetic
  // containerMeta shape the ported TemplatePreview expects — reuse it rather than re-deriving
  // that shape here. `.details` is the piece TemplatePreview's `template` prop wants. Body is
  // normalized above before mapTemplate ever sees it — a shallow copy, mapTemplate itself is
  // untouched and shared with the Message tab, which deliberately keeps Enterprise's own numbering.
  const previewTemplate = template ? mapTemplate({ ...template, body: normalizedBody }, 0).details : null;

  return (
    <Modal
      data={{
        title: template?.name || EM_DASH,
        content: (
          <div className="br-tpl-detail">
            <div className="br-tpl-detail-facts" data-testid="bloomreach-templates-detail-facts">
              <dl className="br-tpl-detail-list">
                <dt>Status</dt>
                <dd>
                  <Chips
                    data={[
                      {
                        label: meta.label,
                        color: TONE_TO_CHIP_COLOR[meta.tone] || 'default',
                        size: 'small',
                        'data-testid': 'bloomreach-templates-detail-status-chip',
                      },
                    ]}
                  />
                </dd>

                <dt>Category</dt>
                <dd data-testid="bloomreach-templates-detail-category">{orDash(template?.category)}</dd>

                <dt>Language</dt>
                <dd data-testid="bloomreach-templates-detail-language">{orDash(template?.language)}</dd>

                <dt>Type</dt>
                <dd data-testid="bloomreach-templates-detail-type">{orDash(template?.type)}</dd>

                <dt>Buttons</dt>
                <dd data-testid="bloomreach-templates-detail-buttons">{buttonsOrDash(template?.button_type)}</dd>

                <dt>Template id</dt>
                <dd data-testid="bloomreach-templates-detail-id">{orDash(template?.id)}</dd>

                <dt>Created</dt>
                <dd data-testid="bloomreach-templates-detail-created">{formatEpochMs(template?.creation_time)}</dd>

                <dt>Updated</dt>
                <dd data-testid="bloomreach-templates-detail-updated">{formatEpochMs(template?.updation_time)}</dd>
              </dl>

              {/*
                rejection_reason IS a real field on the upstream Enterprise HSM template API — ce-api's
                GetWhatsappHSMResponse.rejection_reason, populated from WhatsAppTemplate.getRejectionReason()
                (MessageTemplateService.java, case "REJECTIONREASON"). It is only filled in when the
                caller's `fields` query param requests "rejectionreason". gs-intg-services'
                EnterpriseService.getTemplates — the call behind this app's own api/gsIntg.js
                getTemplates() — only requests ["buttons","previouscategory"], so today's list rows
                never actually carry it. Kept conditional on the real field name (rather than left out
                entirely) so this renders for free the moment that upstream fields list is extended.
              */}
              {template?.rejection_reason && (
                <Alert
                  type="error"
                  title="Rejection reason"
                  description={template.rejection_reason}
                  data-testid="bloomreach-templates-detail-rejection-reason"
                />
              )}

              <dl className="br-tpl-detail-list br-tpl-detail-list--parts">
                <dt>Header</dt>
                <dd data-testid="bloomreach-templates-detail-header">{orDash(template?.header)}</dd>

                <dt>Body</dt>
                <dd data-testid="bloomreach-templates-detail-body">{orDash(normalizedBody)}</dd>

                <dt>Footer</dt>
                <dd data-testid="bloomreach-templates-detail-footer">{orDash(template?.footer)}</dd>
              </dl>
            </div>

            <div className="br-tpl-detail-preview" data-testid="bloomreach-templates-detail-preview">
              {previewTemplate && <TemplatePreview template={previewTemplate} width="100%" height="480px" />}
            </div>
          </div>
        ),
        actionButtonProps: [
          {
            children: 'Close',
            variant: 'outlined',
            onClick: onClose,
            'data-testid': 'bloomreach-templates-detail-close-button',
          },
          {
            children: 'Edit',
            variant: 'contained',
            disabled: !editable,
            // Button's own tooltipText prop wraps itself in a Tooltip when set — this is the
            // design system's built-in mechanism for a disabled-button explanation (List.js's
            // IconButton has no such prop, hence the manual Tooltip+span wrapper used there instead).
            tooltipText: editable ? undefined : 'Only Approved, Rejected or Paused templates can be edited',
            onClick: () => onEdit(template),
            'data-testid': 'bloomreach-templates-detail-edit-button',
          },
        ],
      }}
      config={{
        open: true,
        onClose,
        showCloseIcon: true,
        closeIconTestId: 'bloomreach-templates-detail-close-icon',
        maxWidth: 'md',
        fullWidth: true,
        'data-testid': 'bloomreach-templates-detail-modal',
      }}
    />
  );
};

export default DetailModal;
